import React, { useState, useEffect, useMemo } from 'react';
import { useRouter, useLocalSearchParams, usePathname } from 'expo-router';
import { DataService } from '../services/dataService';
import { Goal, GoalPhase, Milestone } from '../types';
import { ContextualChatButton } from '../components/ContextualChatButton';
import { PlanTimeline } from '../components/PlanTimeline';
import { FormattedGoalDescription } from '../components/FormattedGoalDescription';
import { FrameworkDetailsModal } from '../components/modals/FrameworkDetailsModal';
import { ResearchApplicationModal } from '../components/modals/ResearchApplicationModal';
import { BriefingDetailModal } from '../components/modals/BriefingDetailModal';
import { ThisWeeksPlanCard } from '../components/cards/ThisWeeksPlanCard';
import { storage } from '../services/storage';
import { API_BASE_URL } from '../utils/api'; // Environment-aware API URL
import { useNavigation } from '../contexts/NavigationContext'; // 🔧 FIX JAN 21: Smart back navigation

// 🆕 Research source types (from Research Graph Integration January 2025)
interface ResearchSource {
  id: string;
  title: string;
  authors: string[];
  journal: string;
  year: number;
  doi: string | null;
  key_findings: string[];
  evidence_strength: string;
  consensus_level: number;
  contexts_used: string[];
  citation_count: number;
  citation_text: string;
}

interface ResearchData {
  total_sources: number;
  sources_by_context: Record<string, number>;
  summary: string;
  research_sources: ResearchSource[];
}

interface GoalDetailProps {
  goal: Goal;
  onBack: () => void;
  onPlanClick?: (planId: string) => void;
}

const GoalDetail: React.FC<{ goalId?: string }> = (props) => {
  // 🔧 STRICTMODE FIX (Jan 21, 2026): Handle React reconciliation passes
  const { goalId: propGoalId } = props || {};
  
  const router = useRouter();
  const params = useLocalSearchParams<{ id: string; source?: string; from?: string }>();
  const { id } = params;
  const goalId = propGoalId || id;

  // Navigation context (called unconditionally — Rules of Hooks)
  const navigationContext = useNavigation();

  const getCurrentSource = navigationContext?.getCurrentSource || (() => 'unknown');
  const sourceFromState = params.source;
  const fromChat = params.from === 'chat' ||
                   params.from === 'contextual-launch';

  // 🔧 FIX FEB 4, 2026: Use NavigationContext goBack() for stateful overlay pattern
  const handleSmartBack = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const source = getCurrentSource() || sourceFromState;

    // 🔧 If navigated from chat, go back to chat
    if (fromChat || source === 'chat') {
      router.push('/chat');
      return;
    }

    // MyPath uses stateful overlay pattern — goBack() resets state
    if (navigationContext) {
      
      // Dispatch event to MyPath to set viewMode/entityFilter
      if (source === 'today') {
        window.dispatchEvent(new CustomEvent('navigateBackToMyPath', {
          detail: { source: 'today' }
        }));
      } else if (source === 'browse-goals') {
        window.dispatchEvent(new CustomEvent('navigateBackToMyPath', {
          detail: { source: 'browse-goals' }
        }));
      }

      // Reset NavigationContext state (closes detail overlay)
      navigationContext.goBack();
    } else {
      // Fallback for non-overlay routing
      router.push('/mypath');
    }
  };

  // ── All useState hooks (must be before any conditional returns — Rules of Hooks) ──
  const [researchModalOpen, setResearchModalOpen] = useState(false);
  const [frameworkModalOpen, setFrameworkModalOpen] = useState(false);
  const [selectedPhaseIndex, setSelectedPhaseIndex] = useState<number>(0);
  const [briefingModalOpen, setBriefingModalOpen] = useState(false);
  const [researchData, setResearchData] = useState<ResearchData | null>(null);
  const [researchLoading, setResearchLoading] = useState(false);
  // Memoize goal to prevent new object reference each render (P1: was causing infinite useEffect loop)
  const goal = useMemo(() => goalId ? DataService.getGoalById(goalId) : null, [goalId]);

  // Fetch research data for this goal
  useEffect(() => {
    if (!goalId) return;
    let cancelled = false;
    const fetchResearch = async () => {
      setResearchLoading(true);
      try {
        const token = await storage.getItem('jwt_token');
        const response = await fetch(
          `${API_BASE_URL}/api/goals/${goalId}/research`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` })
            }
          }
        );
        if (!cancelled && response.ok) {
          const data = await response.json();
          setResearchData(data.research);
        }
      } catch (error) {
        // Research fetch is non-critical; fail silently
      } finally {
        if (!cancelled) setResearchLoading(false);
      }
    };
    fetchResearch();
    return () => { cancelled = true; };
  }, [goalId]);

  // ── Early returns AFTER all hooks (Rules of Hooks compliance) ──
  if (!goalId || !goal) {
    return goalId ? <div>Goal not found</div> : null;
  }

  // Unified progress system
  const unifiedProgress = DataService.calculateUnifiedProgress(goal);
  const currentPhase = goal.journey?.phases?.[goal.journey.currentPhase] ?? goal.journey?.phases?.[0];

  if (!currentPhase) {
    return <div className="p-8 text-center text-gray-500">Goal data incomplete — no phases found.</div>;
  }

  const getMilestoneIcon = (milestone: Milestone) => {
    switch (milestone.type) {
      case 'fitness':
      case 'performance':
      case 'skill':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'consistency':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'technique':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        );
      case 'mindset':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
    }
  };

  return (
    <div className="clean-chat-container h-screen bg-white max-w-md mx-auto overflow-y-auto flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0 sticky top-0 z-10 bg-white"
           style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}>
        <button
          onClick={handleSmartBack}
          className="flex items-center space-x-2 text-[var(--cedar-primary)] hover:text-[var(--cedar-dark)] cursor-pointer"
          type="button"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Back</span>
        </button>
        <h1 className="text-xl font-bold text-gray-900">Goal Details</h1>
        
        {/* 🎯 MISSION 29 (Jan 23, 2026): Chat with Cedar button */}
        <ContextualChatButton
          context={{
            type: 'goal',
            id: goal.id,
            title: goal.title,
            description: goal.description,
            metadata: {
              target_date: goal.targetDate,
              event_name: goal.eventName,
              timeline: goal.timeline
            }
          }}
          text=""
          size="small"
          variant="clean"
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 pb-32">
        
        {/* 🎯 GOAL PROFILE HERO - Strategic Analysis Front and Center */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Goal: {goal.title}</h2>
          
          <h3 className="text-lg font-semibold text-gray-900">Goal Profile</h3>
          
          {/* Cedar's Strategic Analysis - The Goal Profile */}
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <div className="p-1.5 bg-[var(--cedar-subtle)] rounded-lg">
                <svg className="w-4 h-4 text-[var(--cedar-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900">Your Briefing</h3>
            </div>
            
            {/* 🆕 JAN 2025: Briefing Summary Section - 3-sentence punchy version */}
            {(goal.briefingSummary || goal.backgroundBriefing) && (
              <div 
                className="mb-4 cursor-pointer hover:bg-[var(--cedar-subtle)] transition-colors rounded-lg p-3 -mx-3"
                onClick={() => setBriefingModalOpen(true)}
              >
                <div className="text-sm text-gray-700 leading-relaxed">
                  {goal.briefingSummary || goal.backgroundBriefing}
                </div>
                {goal.backgroundBriefing && (
                  <div className="mt-2 text-xs text-[var(--cedar-primary)] font-medium flex items-center space-x-1">
                    <span>Review full briefing</span>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            )}
            
            {/* Timeline - Full Width */}
            <div className="mt-4">
              <div className="flex items-center space-x-2 mb-2">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 className="font-medium text-gray-700">Timeline</h4>
              </div>
              {(() => {
                // Target date: normalizer always populates targetDate from backend target_date
                const targetDate = goal.targetDate;
                
                if (targetDate) {
                  const today = new Date();
                  const target = new Date(targetDate);
                  if (isNaN(target.getTime())) {
                    // Invalid date — fall through to timeline fallback
                  } else {
                  const totalDays = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

                  if (totalDays <= 0) {
                    // Past target date — check actual goal status, don't assume completed
                    const isComplete = goal.status === 'completed';
                    return <p className="text-sm text-gray-600">
                      <span className="font-medium">{target.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      {' — '}{isComplete ? 'Completed' : `${Math.abs(totalDays)} day${Math.abs(totalDays) !== 1 ? 's' : ''} overdue`}
                    </p>;
                  }
                  
                  // Calculate months and days (same logic as MyPath.tsx getTimeframe)
                  const years = Math.floor(totalDays / 365);
                  const remainingAfterYears = totalDays - (years * 365);
                  const months = Math.floor(remainingAfterYears / 30);
                  const days = remainingAfterYears - (months * 30);
                  
                  // Format date
                  const formattedDate = target.toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  });
                  
                  // Build countdown string
                  let countdown = '';
                  if (years > 0) {
                    countdown = `${years} year${years > 1 ? 's' : ''}`;
                    if (months > 0) {
                      countdown += `, ${months} month${months > 1 ? 's' : ''}`;
                    }
                  } else if (months > 0) {
                    countdown = `${months} month${months > 1 ? 's' : ''}`;
                    if (days > 0) {
                      countdown += `, ${days} day${days > 1 ? 's' : ''}`;
                    }
                  } else {
                    countdown = `${totalDays} day${totalDays > 1 ? 's' : ''}`;
                  }
                  
                  return (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">{formattedDate}</span>
                      {' - '}
                      <span>{countdown}</span>
                    </p>
                  );
                  } // end else (valid date)
                } else if (goal.timeline) {
                  // Use authoritative timeline field from backend if available
                  return <p className="text-sm text-gray-600">{goal.timeline}</p>;
                } else if (goal.cedarAnalysis?.timeline) {
                  // Fallback to cedarAnalysis timeline (synthesized from target_date)
                  return <p className="text-sm text-gray-600">{goal.cedarAnalysis.timeline}</p>;
                } else {
                  // No timeline data available — show nothing rather than a hardcoded fallback
                  // ⚠️ FLAG FOR PAUL: Confirm authoritative timeline field name from backend
                  return <p className="text-sm text-gray-400 italic">Timeline pending</p>;
                }
              })()}
            </div>
          </div>
        </div>

        {/* 📅 PROGRESS - Broad Bounds for Plans */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-4">
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900">How we'll get there:</h3>
          </div>
          
          {/* 🆕 JAN 19, 2026: Methodology Summary */}
          {/* 🎯 FIX #2: Split methodology into bullets */}
          {goal.framework?.methodology_summary && (
            <div className="mb-4 p-4 bg-[var(--cedar-subtle)] rounded-lg border border-[var(--cedar-primary)]">
              <ul className="text-sm text-gray-700 leading-relaxed space-y-2">
                {goal.framework.methodology_summary.split(/\.\s+/).filter(sentence => sentence.trim()).map((sentence, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="mr-2 text-[var(--cedar-primary)] mt-0.5">•</span>
                    <span>{sentence.trim()}{sentence.trim().endsWith('.') ? '' : '.'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Journey Phase Structure */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">
                Phase {goal.journey.currentPhase + 1} of {goal.journey.phases.length} • {(() => {
                  const current = goal.journey.currentPhase || 0;
                  const total = goal.journey.phases.length || 1;
                  const progressPct = total > 0 ? Math.round((current / total) * 100) : 0;
                  return progressPct;
                })()}% Complete
              </span>
            </div>
            
            {/* Phase Progress Visualization - CLICKABLE */}
            <div className="flex space-x-1 mb-4">
              {goal.journey.phases.map((phase, index) => (
                <div
                  key={index}
                  className={`flex-1 h-3 rounded-full relative cursor-pointer hover:opacity-80 transition-opacity ${
                    index < goal.journey.currentPhase 
                      ? 'bg-green-500' 
                      : index === goal.journey.currentPhase 
                        ? 'bg-[var(--cedar-primary)]' 
                        : 'bg-gray-200'
                  }`}
                  onClick={() => {
                    if (goal.framework) {
                      setSelectedPhaseIndex(index);
                      setFrameworkModalOpen(true);
                    }
                  }}
                  title={`Click to view ${phase.title}`}
                >
                  {index === goal.journey.currentPhase && (
                    <div 
                      className="h-full bg-[var(--cedar-dark)] rounded-full"
                      style={{ width: `${unifiedProgress.journey.phaseProgress}%` }}
                    />
                  )}
                </div>
              ))}
            </div>
            
            {/* 🎯 FIX #3: Current Phase as heading above box */}
            <h4 className="font-bold text-gray-600 mb-2">Current Phase</h4>
            
            {/* Current Phase Box (CLICKABLE) */}
            <div 
              className="bg-[var(--cedar-subtle)] rounded-lg p-4 mb-4 cursor-pointer hover:bg-[var(--cedar-limestone)] transition-colors group border-2 border-[var(--cedar-primary)]"
              onClick={() => {
                if (goal.framework) {
                  setSelectedPhaseIndex(goal.journey.currentPhase);
                  setFrameworkModalOpen(true);
                }
              }}
            >
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-6 h-6 bg-[var(--cedar-primary)] rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {goal.journey.currentPhase + 1}
                </div>
                <span className="font-medium text-[var(--cedar-dark)]">{currentPhase.title}</span>
              </div>
              <p className="text-sm text-gray-700 mb-3">{currentPhase.description}</p>
              
              {/* 🆕 JAN 19, 2026: Phase Objectives */}
              {(() => {
                const currentPhaseData = (goal.framework?.phases as any)?.[goal.journey.currentPhase];
                const keyObjectives: string[] = currentPhaseData?.key_objectives;
                if (!keyObjectives || keyObjectives.length === 0) return null;
                return (
                  <div className="mt-3 pt-3 border-t border-gray-300">
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">Phase Objectives:</h4>
                    <ul className="space-y-1">
                      {keyObjectives.map((objective: string, idx: number) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <span className="mr-2 text-[var(--cedar-primary)]">☐</span>
                          {objective}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}
              
              {/* 🆕 JAN 19, 2026: Phase Targets */}
              {(() => {
                const currentPhaseData = (goal.framework?.phases as any)?.[goal.journey.currentPhase];
                const targetMetrics = currentPhaseData?.target_metrics;
                if (!targetMetrics) return null;
                const volume = targetMetrics.volume as any;
                return (
                  <div className="mt-3 pt-3 border-t border-gray-300">
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">Phase Targets:</h4>
                    <div className="space-y-1 text-sm text-gray-700">
                      {targetMetrics.performance?.target_explanation && (
                        <div>
                          <span className="font-medium text-[var(--cedar-primary)]">Performance:</span> {targetMetrics.performance.target_explanation}
                        </div>
                      )}
                      {targetMetrics.cardiovascular?.target_explanation && (
                        <div>
                          <span className="font-medium text-[var(--cedar-primary)]">Cardiovascular:</span> {targetMetrics.cardiovascular.target_explanation}
                        </div>
                      )}
                      {targetMetrics.strength?.target_explanation && (
                        <div>
                          <span className="font-medium text-[var(--cedar-primary)]">Strength:</span> {targetMetrics.strength.target_explanation}
                        </div>
                      )}
                      {volume && (
                        <div>
                          <span className="font-medium text-[var(--cedar-primary)]">Volume:</span>{' '}
                          {volume.weekly_runs && `${volume.weekly_runs} runs/week`}
                          {volume.weekly_distance_km_min && volume.weekly_distance_km_max && `, ${volume.weekly_distance_km_min}-${volume.weekly_distance_km_max}km`}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              
              {/* Phase Progress */}
              <div className="flex items-center justify-between text-sm mt-3 pt-3 border-t border-gray-300">
                <span className="text-gray-600">Phase Progress:</span>
                <span className="font-semibold text-[var(--cedar-dark)]">{Math.round(unifiedProgress.journey.phaseProgress)}%</span>
              </div>
              
              {/* 🆕 Click indicator */}
              <div className="mt-2 text-xs text-[var(--cedar-primary)] group-hover:text-[var(--cedar-dark)] font-medium">
                Click to view full framework details →
              </div>
            </div>

            {/* 🆕 JAN 19, 2026: Key Milestones (True Milestones, Not Phases) */}
            {/* 🎯 FIX #4: Redesigned milestone styling with green square + checkbox */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Key Milestones</h4>
              
              {goal.framework?.milestones && goal.framework.milestones.length > 0 ? (
                <div className="space-y-3">
                  {goal.framework.milestones
                    .sort((a, b) => (((a as any).week as number) || a.targetWeek || 0) - (((b as any).week as number) || b.targetWeek || 0))  // 🔧 FEB 1: Support both field names
                    .slice(0, 5) // Show first 5 upcoming
                    .map((milestone) => {
                      const weekNumber = ((milestone as any).week as number) || milestone.targetWeek || 0;  // 🔧 FEB 1: Use "week" field from database
                      const criteria = ((milestone as any).success_metrics as string[]) || milestone.success_criteria || [];  // 🔧 FEB 1: Support both field names
                      
                      return (
                        <div 
                          key={milestone.id || milestone.title}
                          className="flex items-stretch rounded-lg border border-gray-200 overflow-hidden hover:border-[var(--cedar-primary)] transition-colors"
                        >
                          {/* Left: Green square with Week X */}
                          <div className="bg-[var(--cedar-primary)] flex items-center justify-center px-4 py-3 min-w-[80px]">
                            <span className="text-white font-bold text-sm text-center leading-tight">
                              Week<br />{weekNumber}
                            </span>
                          </div>
                          
                          {/* Middle: Milestone content */}
                          <div className="flex-1 p-3 bg-white">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 pr-3">
                                <span className="text-sm font-medium text-gray-900 block mb-1">{milestone.title || (milestone as any).name}</span>
                                {criteria.length > 0 && (
                                  <ul className="mt-1 space-y-0.5">
                                    {criteria.slice(0, 2).map((criterion: string, idx: number) => (
                                      <li key={idx} className="text-xs text-gray-600 flex items-start">
                                        <span className="mr-1 text-[var(--cedar-primary)]">•</span>
                                        {criterion}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              
                              {/* Right: Visual status indicator (not interactive — milestones are tracked by Cedar) */}
                              <div className="pl-2">
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                  (milestone.achieved || (milestone as any).isCompleted)
                                    ? 'border-[var(--cedar-primary)] bg-[var(--cedar-primary)]'
                                    : 'border-gray-300'
                                }`}>
                                  {(milestone.achieved || (milestone as any).isCompleted) && (
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Fallback: Show phases if no milestones */}
                  {goal.journey.phases.map((phase, index) => (
                    <div 
                      key={index} 
                      className={`flex items-start space-x-3 p-3 rounded-lg cursor-pointer transition-all ${
                        index < goal.journey.currentPhase 
                          ? 'bg-green-50 border border-green-200 hover:bg-green-100' 
                          : index === goal.journey.currentPhase 
                            ? 'bg-[var(--cedar-subtle)] border-2 border-[var(--cedar-primary)] hover:bg-[var(--cedar-limestone)]' 
                            : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                      }`}
                      onClick={() => {
                        if (goal.framework) {
                          setSelectedPhaseIndex(index);
                          setFrameworkModalOpen(true);
                        }
                      }}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index < goal.journey.currentPhase 
                          ? 'bg-green-500 text-white' 
                          : index === goal.journey.currentPhase 
                            ? 'bg-[var(--cedar-primary)] text-white' 
                            : 'bg-gray-300 text-gray-600'
                      }`}>
                      {index < goal.journey.currentPhase ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className={`font-medium ${
                          index < goal.journey.currentPhase 
                            ? 'text-green-800' 
                            : index === goal.journey.currentPhase 
                              ? 'text-[var(--cedar-dark)]' 
                              : 'text-gray-700'
                        }`}>
                          {phase.title}
                        </span>
                        {index === goal.journey.currentPhase && (
                          <span className="px-2 py-1 bg-[var(--cedar-subtle)] text-[var(--cedar-primary)] text-xs font-medium rounded-full border border-[var(--cedar-primary)]">
                            Current
                          </span>
                        )}
                      </div>
                      <p className={`text-sm ${
                        index < goal.journey.currentPhase 
                          ? 'text-green-700' 
                          : index === goal.journey.currentPhase 
                            ? 'text-[var(--cedar-primary)]' 
                            : 'text-gray-600'
                      }`}>
                        {phase.description}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Estimated Duration: {phase.estimatedWeeks} weeks
                      </p>
                      {/* 🆕 Click indicator */}
                      <p className="text-xs text-gray-400 mt-1">
                        Click to view details →
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          </div>
        </div>

        {/* 📚 RESEARCH SOURCES - ALWAYS VISIBLE */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="p-1.5 bg-green-100 rounded-lg">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900">Research-Backed</h3>
            </div>
            {!researchLoading && researchData && researchData.total_sources > 0 && (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                {researchData.total_sources} {researchData.total_sources === 1 ? 'Source' : 'Sources'}
              </span>
            )}
          </div>
          
          {researchLoading ? (
            <div className="text-sm text-gray-600 mb-3">
              <div className="animate-pulse flex space-x-2">
                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              </div>
            </div>
          ) : researchData && researchData.total_sources > 0 ? (
            <>
              <p className="text-sm text-gray-700 mb-3">
                Your plan is backed by peer-reviewed research across {Object.keys(researchData.sources_by_context || {}).length} areas of training science.
              </p>
              
              <button
                onClick={() => setResearchModalOpen(true)}
                className="w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700 font-medium transition-colors flex items-center justify-center space-x-2"
              >
                <span>View Research Sources</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 mb-2">Research sources will be added as your plan develops</p>
              <p className="text-xs text-gray-500">Cedar analyzes peer-reviewed research to back your training</p>
            </div>
          )}
        </div>

        {/* 🎯 FIX #6: Full Weekly Plan Card (same as Today section) */}
        {/* 🔧 FIX JAN 21: Use navigateToDetail from NavigationContext to prevent reload */}
        {/* 🎨 JAN 26, 2026: Single card pattern (no wrapper - matches SimpleWorkoutCard structure) */}
        <ThisWeeksPlanCard onItemClick={(type, id) => {
          if (type === 'plan') {
            // Navigate to plan detail page properly
            router.push(`/plan/${id}`);
          }
        }} />
      </div>
      
      {/* 🆕 OCT 26, 2025: Issue 5 - Framework Details Modal */}
      {frameworkModalOpen && goal.framework && (
        <FrameworkDetailsModal
          isOpen={frameworkModalOpen}
          onClose={() => setFrameworkModalOpen(false)}
          framework={goal.framework}
          goalTitle={goal.title}
          goalId={goalId}
          initialPhaseIndex={selectedPhaseIndex}
        />
      )}
      
      {/* 🆕 JAN 2025: Briefing Detail Modal */}
      {briefingModalOpen && goal.backgroundBriefing && (
        <BriefingDetailModal
          isOpen={briefingModalOpen}
          onClose={() => setBriefingModalOpen(false)}
          briefing={goal.backgroundBriefing}
          goalTitle={goal.title}
        />
      )}
      
      {/* 🆕 JAN 2025: Research Sources Modal */}
      {researchModalOpen && goalId && (
        <ResearchApplicationModal
          goalId={goalId}
          goalTitle={goal.title}
          isOpen={researchModalOpen}
          onClose={() => setResearchModalOpen(false)}
        />
      )}
    </div>
  );
};

export default GoalDetail; 